import { ID } from '../../models/id';

export type Money = string;
type DecimalString = string;
type ISODate = string;
type ISODateTime = string;

export enum LedgerProvisioningStatusEnum {
  NOT_PROVISIONED = 'not_provisioned',
  PROVISIONING = 'provisioning',
  IMPORTING = 'importing',
  READY = 'ready',
  FAILED = 'failed',
}

export enum LedgerAccountTypeEnum {
  ASSET = 'asset',
  LIABILITY = 'liability',
  EQUITY = 'equity',
  REVENUE = 'revenue',
  EXPENSE = 'expense',
}

export const LEDGER_ACCOUNT_TYPE_LABELS: Record<LedgerAccountTypeEnum, string> = {
  [LedgerAccountTypeEnum.ASSET]: 'Asset',
  [LedgerAccountTypeEnum.LIABILITY]: 'Liability',
  [LedgerAccountTypeEnum.EQUITY]: 'Equity',
  [LedgerAccountTypeEnum.REVENUE]: 'Revenue',
  [LedgerAccountTypeEnum.EXPENSE]: 'Expense',
};

export enum LedgerNormalBalanceEnum {
  DEBIT = 'debit',
  CREDIT = 'credit',
}

export interface LedgerSubcategoryOption {
  value: string;
  label: string;
  financial_class: string;
  financial_class_label: string;
  type: LedgerAccountTypeEnum;
  normal_balance: LedgerNormalBalanceEnum;
  selectable: boolean;
}

export enum LedgerKpiValueTypeEnum {
  CURRENCY = 'currency',
  PERCENT = 'percent',
  RATIO = 'ratio',
}

export enum LedgerTagKindEnum {
  JOB = 'job',
  VENDOR = 'vendor',
  CUSTOMER = 'customer',
  COST_CODE = 'cost_code',
}

export enum JournalEntryStatusEnum {
  DRAFT = 'draft',
  POSTING = 'posting',
  POSTED = 'posted',
  FAILED = 'failed',
}

export enum LedgerReportKeyEnum {
  PROFIT_AND_LOSS = 'profit-and-loss',
  BALANCE_SHEET = 'balance-sheet',
  GENERAL_LEDGER = 'general-ledger',
  TRIAL_BALANCE = 'trial-balance',
}

export enum LedgerReportRowKindEnum {
  ACCOUNT = 'account',
  LINE = 'line',
  SUBTOTAL = 'subtotal',
  TOTAL = 'total',
}

export enum LedgerExportFormatEnum {
  CSV = 'csv',
  PDF = 'pdf',
  XLSX = 'xlsx',
}

export enum LedgerExportStatusEnum {
  QUEUED = 'queued',
  RUNNING = 'running',
  READY = 'ready',
  FAILED = 'failed',
}

export interface Kpi {
  key: string;
  title: string;
  amount: Money | DecimalString;
  value_type: LedgerKpiValueTypeEnum;
}

export interface Tag {
  kind: LedgerTagKindEnum;
  id: ID;
  name: string;
}

interface TagInput {
  kind: LedgerTagKindEnum;
  id: ID;
}

interface Attachment {
  id: ID;
  name: string;
  url: string;
  uploaded_at: ISODateTime;
}

interface LedgerSource {
  type: string;
  id: ID | null;
  label: string | null;
  attachment?: { name: string; url: string } | null;
  review_item?: { id: ID; label: string } | null;
}

interface UserRef {
  id: ID;
  name: string;
}

interface LedgerError {
  code: string;
  message: string;
  step?: string;
  at: ISODateTime;
}

export interface ExportJob {
  id: ID;
  status: LedgerExportStatusEnum;
  url: string | null;
}

interface LedgerEntity {
  id: ID;
  name: string;
  entity_type: string | null;
  status: LedgerProvisioningStatusEnum;
  coa_template_version: string | null;
  provisioned_at: ISODateTime | null;
  import: { source: string; cutover_date: ISODate; status: string } | null;
  last_error: LedgerError | null;
}

export interface LedgerStatus {
  status: LedgerProvisioningStatusEnum;
  targets: string[];
  entities: LedgerEntity[];
  capabilities: string[];
  failed_entries: number;
  max_account_depth: number;
  links: { managed_bookkeeping: string | null };
}

export interface LedgerAccount {
  id: ID;
  number: number;
  sub_number: number | null;
  code: string;
  position: number;
  name: string;
  type: LedgerAccountTypeEnum;
  category: string;
  subcategory: string | null;
  normal_balance: LedgerNormalBalanceEnum;
  parent_id: ID | null;
  depth: number;
  is_system: boolean;
  archived: boolean;
  balance: Money;
  children: LedgerAccount[];
}

export interface LedgerAccountsResponse {
  kpis: Kpi[];
  accounts: LedgerAccount[];
  subcategories: LedgerSubcategoryOption[];
}

export interface LedgerAccountsQuery {
  entity?: ID | null;
  include_archived?: boolean;
  flat?: boolean;
  type?: LedgerAccountTypeEnum;
}

export interface LedgerAccountCreate {
  name: string;
  subcategory: string;
  normal_balance: LedgerNormalBalanceEnum;
  type?: LedgerAccountTypeEnum;
  parent_id?: ID | null;
}

export interface LedgerAccountUpdate {
  name?: string;
  parent_id?: ID | null;
  position?: number;
  archived?: boolean;
}

export interface LedgerAccountUpdateResponse {
  account: LedgerAccount;
  renumbered: LedgerAccount[];
}

export interface JournalLine {
  id: ID;
  account_id: ID;
  account_code: string;
  account_name: string;
  side: LedgerNormalBalanceEnum;
  amount: Money;
  description: string;
  tags: Tag[];
}

interface LedgerPosting {
  ledger: string;
  status: JournalEntryStatusEnum;
  attempts: number;
  next_retry_at: ISODateTime | null;
  last_error: LedgerError | null;
  posted_at: ISODateTime | null;
}

export interface JournalEntry {
  id: ID;
  number: string;
  date: ISODate;
  memo: string;
  status: JournalEntryStatusEnum;
  editable: boolean;
  source: LedgerSource;
  tags: Tag[];
  lines: JournalLine[];
  total_debits: Money;
  total_credits: Money;
  postings: LedgerPosting[];
  attachments: Attachment[];
  reversal_of: ID | null;
  reversed_by: ID | null;
  last_error: LedgerError | null;
  created_by: UserRef;
  created_at: ISODateTime;
  posted_by: UserRef | null;
  posted_at: ISODateTime | null;
}

export interface JournalEntryLineInput {
  account_id: ID;
  side: LedgerNormalBalanceEnum;
  amount: Money;
  description?: string;
}

export interface JournalEntryInput {
  entity_id?: ID | null;
  date: ISODate;
  memo?: string;
  tags?: TagInput[];
  lines: JournalEntryLineInput[];
}

export interface LedgerReportQuery {
  period: string;
  job_id?: ID | null;
  vendor_id?: ID | null;
  customer_id?: ID | null;
  entity?: ID | 'all' | null;
}

export interface LedgerReportRow {
  kind: LedgerReportRowKindEnum;
  key?: string;
  account_id?: ID;
  code?: string;
  name: string;
  amount?: Money;
  values?: Record<string, Money | DecimalString | string | null>;
  children?: LedgerReportRow[];
}

export interface LedgerReportSection {
  key: string;
  title: string;
  rows: LedgerReportRow[];
}

interface LedgerReportColumn {
  key: string;
  title: string;
}

export interface LedgerReport {
  period: { key: string; start: ISODate; end: ISODate };
  basis: string;
  generated_at: ISODateTime;
  kpis: Kpi[];
  columns?: LedgerReportColumn[];
  sections: LedgerReportSection[];
}

export interface AccountPosting {
  date: ISODate;
  journal_entry_id: ID | null;
  entry_number: string | null;
  source: LedgerSource;
  name: string;
  job: { id: ID; name: string } | null;
  debit: Money | null;
  credit: Money | null;
  running_balance: Money;
}

export interface AccountPostings {
  account: { id: ID; code: string; name: string };
  kpis: Kpi[];
  results: AccountPosting[];
  next: string | null;
}
